import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import AdvertisingBanner from '@/components/AdvertisingBanner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Music, Video, DollarSign, Youtube, 
  TrendingUp, BarChart, HeadphonesIcon, Sparkles, Loader2, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, isAdminAdil } = useAuth();
  const navigate = useNavigate();
  const [selectedModal, setSelectedModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [artists, setArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);

  // Formulaire 1 - Créer un Artiste
  const [form1Data, setForm1Data] = useState({
    artist_name: '',
    artist_type: '',
    country: '',
    city: '',
    instagram: '',
    facebook: '',
    soundcloud: '',
    audiomack: ''
  });

  // Formulaire 2 - Distribution Chanson
  const [form2Data, setForm2Data] = useState({
    title: '',
    artist_id: '',
    release_type: '',
    main_genre: '',
    sub_genre: '',
    release_date: '',
    pre_release_date: '',
    description: '',
    main_country: 'DRC',
    audio_file: null,
    cover_image: null
  });

  // Formulaire 3 - Distribution Vidéo
  const [form3Data, setForm3Data] = useState({
    video_title: '',
    artist_id: '',
    featured_artists: '',
    genre: '',
    release_date: '',
    language: '',
    main_country: '',
    associated_track: '',
    video_file: null,
    thumbnail: null
  });

  // Charger les artistes
  const fetchArtists = async () => {
    setLoadingArtists(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/distribution/artists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setArtists(data.artists || []);
      }
    } catch (error) {
      console.error('Erreur chargement artistes:', error);
    } finally {
      setLoadingArtists(false);
    }
  };

  useEffect(() => {
    if (selectedModal === 'song' || selectedModal === 'video') {
      fetchArtists();
    }
  }, [selectedModal]);

  const distroServices = [
    {
      id: 'artist',
      title: 'Créer ma chaîne artiste VEVO',
      price: 150,
      icon: User,
      description: 'Chaîne Artiste VEVO avec badge officiel'
    },
    {
      id: 'song',
      title: 'Distribuez votre chanson sur toutes les plateformes',
      price: 30,
      icon: Music,
      description: 'Spotify, Apple Music, Deezer, YouTube Music, etc.'
    },
    {
      id: 'video',
      title: 'Vendez vos vidéos / clips sur Vevo & co pour gagner de l\'argent',
      price: 25,
      icon: Video,
      description: 'VEVO, Apple Music, TIDAL - Gagnez 70% des revenus'
    }
  ];

  const handleOpenModal = (serviceId) => {
    setSelectedModal(serviceId);
    // Réinitialiser les formulaires
    setForm1Data({
      artist_name: '',
      artist_type: '',
      country: '',
      city: '',
      instagram: '',
      facebook: '',
      soundcloud: '',
      audiomack: ''
    });
    setForm2Data({
      title: '',
      artist_id: '',
      release_type: '',
      main_genre: '',
      sub_genre: '',
      release_date: '',
      pre_release_date: '',
      description: '',
      main_country: 'DRC',
      audio_file: null,
      cover_image: null
    });
    setForm3Data({
      video_title: '',
      artist_id: '',
      featured_artists: '',
      genre: '',
      release_date: '',
      language: '',
      main_country: '',
      associated_track: '',
      video_file: null,
      thumbnail: null
    });
  };

  const closeModal = () => {
    setSelectedModal(null);
  };

  // Soumission Formulaire 1 - Créer un Artiste
  const handleSubmitForm1 = async (e) => {
    e.preventDefault();
    
    if (!form1Data.artist_name || !form1Data.artist_type || !form1Data.country || !form1Data.city) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const formDataToSend = new FormData();
      formDataToSend.append('artist_name', form1Data.artist_name);
      formDataToSend.append('artist_type', form1Data.artist_type);
      formDataToSend.append('country', form1Data.country);
      formDataToSend.append('city', form1Data.city);
      formDataToSend.append('instagram', form1Data.instagram || '');
      formDataToSend.append('facebook', form1Data.facebook || '');
      formDataToSend.append('soundcloud', form1Data.soundcloud || '');
      formDataToSend.append('audiomack', form1Data.audiomack || '');

      const response = await fetch(`${API_URL}/api/distribution/create-artist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        toast.success('🎉 Artiste créé avec succès! Vous pouvez maintenant distribuer vos chansons et vidéos.');
        closeModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Soumission Formulaire 2 - Distribution Chanson
  const handleSubmitForm2 = async (e) => {
    e.preventDefault();
    
    if (!form2Data.title || !form2Data.artist_id || !form2Data.release_type || 
        !form2Data.main_genre || !form2Data.release_date || !form2Data.audio_file || !form2Data.cover_image) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const formDataToSend = new FormData();
      formDataToSend.append('title', form2Data.title);
      formDataToSend.append('artist_id', form2Data.artist_id);
      formDataToSend.append('release_type', form2Data.release_type);
      formDataToSend.append('main_genre', form2Data.main_genre);
      formDataToSend.append('sub_genre', form2Data.sub_genre || '');
      formDataToSend.append('release_date', form2Data.release_date);
      formDataToSend.append('pre_release_date', form2Data.pre_release_date || '');
      formDataToSend.append('description', form2Data.description || '');
      formDataToSend.append('main_country', form2Data.main_country);
      formDataToSend.append('audio_file', form2Data.audio_file);
      formDataToSend.append('cover_image', form2Data.cover_image);

      const response = await fetch(`${API_URL}/api/distribution/song`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        toast.success('🎵 Distribution de chanson créée avec succès!');
        closeModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la distribution');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Soumission Formulaire 3 - Distribution Vidéo
  const handleSubmitForm3 = async (e) => {
    e.preventDefault();
    
    if (!form3Data.video_title || !form3Data.artist_id || !form3Data.genre || 
        !form3Data.release_date || !form3Data.language || !form3Data.main_country || 
        !form3Data.video_file || !form3Data.thumbnail) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const formDataToSend = new FormData();
      formDataToSend.append('video_title', form3Data.video_title);
      formDataToSend.append('artist_id', form3Data.artist_id);
      formDataToSend.append('featured_artists', form3Data.featured_artists || '');
      formDataToSend.append('genre', form3Data.genre);
      formDataToSend.append('release_date', form3Data.release_date);
      formDataToSend.append('language', form3Data.language);
      formDataToSend.append('main_country', form3Data.main_country);
      formDataToSend.append('associated_track', form3Data.associated_track || '');
      formDataToSend.append('video_file', form3Data.video_file);
      formDataToSend.append('thumbnail', form3Data.thumbnail);

      const response = await fetch(`${API_URL}/api/distribution/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        toast.success('🎬 Distribution vidéo créée avec succès!');
        closeModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la distribution');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      id: 'ebooks',
      title: 'VENDRE MES LIVRES NUMÉRIQUES',
      price: isAdminAdil ? 'GRATUIT' : 'GRATUIT',
      icon: () => <span className="text-3xl">📚</span>,
      color: 'from-yellow-600 to-amber-800',
      description: 'Publie et vends tes ebooks/PDFs sur AGRP BOX',
      buttonText: 'PUBLIER UN LIVRE',
      action: () => navigate('/vendre-livres')
    },
    {
      id: 'adsense',
      title: 'CRÉER MON ADSENSE',
      price: isAdminAdil ? 'GRATUIT' : '150$',
      icon: DollarSign,
      color: isAdminAdil ? 'from-green-600 to-emerald-600' : 'from-orange-600 to-orange-800',
      description: 'Monétise ton contenu YouTube',
      buttonText: isAdminAdil ? 'GRATUIT (Admin Adil)' : 'DÉPOSER MA DEMANDE',
      action: () => navigate('/creer-adsense')
    },
    {
      id: 'live_streaming',
      title: 'PASSER MA VIDÉO EN LIVE',
      price: isAdminAdil ? 'GRATUIT' : '50$',
      icon: BarChart,
      color: isAdminAdil ? 'from-green-600 to-emerald-600' : 'from-cyan-600 to-cyan-800',
      description: '+3000 personnes sur votre vidéo en direct',
      buttonText: isAdminAdil ? 'GRATUIT (Admin Adil)' : 'RÉSERVER MON LIVE',
      action: () => navigate('/live-streaming')
    }
  ];

  const musicGenres = [
    'Acoustic Blues', 'Afrobeat', 'Afro Pop', 'Alternative', 'Blues', 'Classical', 
    'Country', 'Dance', 'Drill', 'Electronic', 'Folk', 'Hip-Hop', 'House', 'Indie', 
    'Jazz', 'K-Pop', 'Latin', 'Metal', 'Pop', 'R&B', 'Rap', 'Reggae', 'Rock', 'Rumba', 'Soul'
  ];

  return (
    <div className="min-h-screen pb-20">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre Principal */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: 'Outfit' }}>
            Bienvenue <span className="text-[#E50914]">{user?.full_name || 'Artiste'}</span> 👋
          </h1>
          <p className="text-zinc-400 text-lg">
            Choisis ton service et lance ta carrière musicale maintenant
          </p>
        </div>

        {/* Bannière Publicitaire Tournante */}
        <AdvertisingBanner />

        {/* 8 Gros Boutons Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {/* GRANDE CARTE DISTRIBUTION MUSICALE & CHAÎNE ARTISTE */}
          <Card className="glass border-zinc-800 hover:border-purple-500 md:col-span-2 lg:col-span-3">
            <div className="h-2 bg-gradient-to-r from-purple-600 to-pink-600"></div>
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <Music size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  DISTRIBUTION MUSICALE & CHAÎNE ARTISTE
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {distroServices.map((service) => {
                  const Icon = service.icon;
                  const displayPrice = isAdminAdil ? 'GRATUIT' : `${service.price}$`;
                  const displayButton = isAdminAdil ? 'GRATUIT (Admin)' : 'Commander';
                  return (
                    <div key={service.id} className="bg-zinc-900/50 p-5 rounded-lg border border-zinc-700 hover:border-purple-500 transition-all">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                          <Icon size={24} className="text-white" />
                        </div>
                        <span className={`text-3xl font-bold ${isAdminAdil ? 'text-green-400' : 'text-purple-400'}`}>
                          {displayPrice}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold mb-2 text-base leading-tight min-h-[3rem]">
                        {service.title}
                      </h3>
                      <p className="text-zinc-400 text-xs mb-4 min-h-[2.5rem]">
                        {service.description}
                      </p>
                      <Button
                        onClick={() => handleOpenModal(service.id)}
                        className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white font-semibold py-3`}
                      >
                        {displayButton}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* AUTRES SERVICES */}
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <Card 
                key={service.id}
                className="glass border-zinc-800 hover:border-[#E50914] transition-all cursor-pointer group overflow-hidden"
                onClick={service.action}
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`}></div>
                <CardContent className="p-8 text-center">
                  <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon size={40} className="text-white" />
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-2 group-hover:text-[#E50914] transition-colors">
                    {service.title}
                  </h2>
                  
                  <p className="text-zinc-400 text-sm mb-4">
                    {service.description}
                  </p>
                  
                  <div className="text-3xl font-bold text-[#F97316] mb-4">
                    {service.price}
                  </div>
                  
                  <Button 
                    className={`w-full bg-gradient-to-r ${service.color} hover:opacity-90 text-white font-semibold py-6 text-lg`}
                  >
                    {service.buttonText}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Section Promotion - Services de Promotion Streaming */}
        <div className="mt-12 border-t border-zinc-800 pt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
            Faites la promotion de vos chansons sur
          </h2>
          
          {/* Grille des services de promotion */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Spotify */}
            <Card className="glass border-green-500/30 hover:border-green-500 transition-all cursor-pointer" onClick={() => navigate('/promouvoir-spotify')}>
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Music className="text-white" size={40} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Promouvoir votre compte Spotify</h3>
                <p className="text-zinc-400 text-sm mb-4">Faites la promotion pour avoir des Hits, abonnés, et entrées en playlists.</p>
                <p className={`${isAdminAdil ? 'text-green-400' : 'text-green-400'} font-bold text-2xl mb-4`}>
                  {isAdminAdil ? 'GRATUIT' : '35$'}
                </p>
                <Button className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-green-600 hover:bg-green-700'}`}>
                  {isAdminAdil ? 'GRATUIT (Admin Adil)' : 'Promouvoir - 35$'}
                </Button>
              </CardContent>
            </Card>

            {/* Apple Music / iTunes */}
            <Card className="glass border-red-500/30 hover:border-red-500 transition-all cursor-pointer" onClick={() => navigate('/promouvoir-apple-music')}>
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Music className="text-white" size={40} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Promouvoir votre compte Apple Music / iTunes</h3>
                <p className="text-zinc-400 text-sm mb-4">Gagnez en visibilité sur Apple Music et iTunes.</p>
                <p className={`${isAdminAdil ? 'text-green-400' : 'text-red-400'} font-bold text-2xl mb-4`}>
                  {isAdminAdil ? 'GRATUIT' : '35$'}
                </p>
                <Button className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-pink-600 to-red-600 hover:opacity-90'}`}>
                  {isAdminAdil ? 'GRATUIT (Admin Adil)' : 'Promouvoir - 35$'}
                </Button>
              </CardContent>
            </Card>

            {/* Boomplay */}
            <Card className="glass border-orange-500/30 hover:border-orange-500 transition-all cursor-pointer" onClick={() => navigate('/promouvoir-boomplay')}>
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Music className="text-white" size={40} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Promouvoir votre compte Boomplay</h3>
                <p className="text-zinc-400 text-sm mb-4">Développez votre audience sur Boomplay.</p>
                <p className={`${isAdminAdil ? 'text-green-400' : 'text-orange-400'} font-bold text-2xl mb-4`}>
                  {isAdminAdil ? 'GRATUIT' : '35$'}
                </p>
                <Button className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-orange-600 to-yellow-600 hover:opacity-90'}`}>
                  {isAdminAdil ? 'GRATUIT (Admin Adil)' : 'Promouvoir - 35$'}
                </Button>
              </CardContent>
            </Card>

            {/* Tidal */}
            <Card className="glass border-blue-500/30 hover:border-blue-500 transition-all cursor-pointer" onClick={() => navigate('/promouvoir-tidal')}>
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Music className="text-white" size={40} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Promouvoir votre compte Tidal</h3>
                <p className="text-zinc-400 text-sm mb-4">Augmentez votre présence sur Tidal.</p>
                <p className={`${isAdminAdil ? 'text-green-400' : 'text-blue-400'} font-bold text-2xl mb-4`}>
                  {isAdminAdil ? 'GRATUIT' : '35$'}
                </p>
                <Button className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {isAdminAdil ? 'GRATUIT (Admin Adil)' : 'Promouvoir - 35$'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Liens Secondaires en Bas */}
        <div className="border-t border-zinc-800 pt-8">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <button 
              onClick={() => navigate('/redevance')}
              className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <TrendingUp size={28} />
              <span className="text-sm font-medium">Redevance</span>
            </button>
            
            <button 
              onClick={() => navigate('/analytique')}
              className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <BarChart size={28} />
              <span className="text-sm font-medium">Analytique</span>
            </button>
            
            <button 
              onClick={() => navigate('/contact')}
              className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <HeadphonesIcon size={28} />
              <span className="text-sm font-medium">Support</span>
            </button>
          </div>
        </div>
      </main>
      
      {/* MODAL FORMULAIRE 1 - CRÉER UN ARTISTE (150$) */}
      <Dialog open={selectedModal === 'artist'} onOpenChange={closeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              🎤 CRÉER UN ARTISTE - {isAdminAdil ? 'GRATUIT (Admin)' : '150$'}
            </DialogTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Créez votre chaîne artiste VEVO
            </p>
          </DialogHeader>
          
          <form onSubmit={handleSubmitForm1} className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Nom de l'artiste *</Label>
              <Input
                type="text"
                placeholder="Ex: Burna Boy"
                value={form1Data.artist_name}
                onChange={(e) => setForm1Data(prev => ({ ...prev, artist_name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Type d'artiste *</Label>
              <Select
                value={form1Data.artist_type}
                onValueChange={(value) => setForm1Data(prev => ({ ...prev, artist_type: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionnez un type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectItem value="Chanteur">Chanteur</SelectItem>
                  <SelectItem value="Rappeur">Rappeur</SelectItem>
                  <SelectItem value="Groupe">Groupe</SelectItem>
                  <SelectItem value="Producteur">Producteur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Pays *</Label>
                <Input
                  type="text"
                  placeholder="Ex: Congo (DRC)"
                  value={form1Data.country}
                  onChange={(e) => setForm1Data(prev => ({ ...prev, country: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">Ville *</Label>
                <Input
                  type="text"
                  placeholder="Ex: Kinshasa"
                  value={form1Data.city}
                  onChange={(e) => setForm1Data(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Instagram</Label>
              <Input
                type="text"
                placeholder="@username"
                value={form1Data.instagram}
                onChange={(e) => setForm1Data(prev => ({ ...prev, instagram: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Facebook</Label>
              <Input
                type="text"
                placeholder="Lien Facebook"
                value={form1Data.facebook}
                onChange={(e) => setForm1Data(prev => ({ ...prev, facebook: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">SoundCloud</Label>
              <Input
                type="text"
                placeholder="Lien SoundCloud"
                value={form1Data.soundcloud}
                onChange={(e) => setForm1Data(prev => ({ ...prev, soundcloud: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Audiomack</Label>
              <Input
                type="text"
                placeholder="Lien Audiomack"
                value={form1Data.audiomack}
                onChange={(e) => setForm1Data(prev => ({ ...prev, audiomack: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white font-bold py-6 text-lg`}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Création en cours...
                </>
              ) : (
                isAdminAdil ? 'GRATUIT (Admin) - Créer l\'artiste' : 'CRÉER L\'ARTISTE - 150$'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL FORMULAIRE 2 - DISTRIBUTION CHANSON (30$) */}
      <Dialog open={selectedModal === 'song'} onOpenChange={closeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              🎵 DISTRIBUTION CHANSON - {isAdminAdil ? 'GRATUIT (Admin)' : '30$'}
            </DialogTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Distribuez votre chanson sur Spotify, Apple Music, Deezer, etc.
            </p>
          </DialogHeader>
          
          <form onSubmit={handleSubmitForm2} className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Titre de la version *</Label>
              <Input
                type="text"
                placeholder="Ex: Mon nouveau single"
                value={form2Data.title}
                onChange={(e) => setForm2Data(prev => ({ ...prev, title: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Libérer l'artiste *</Label>
              {loadingArtists ? (
                <div className="text-zinc-400 text-sm">Chargement des artistes...</div>
              ) : artists.length === 0 ? (
                <div className="text-yellow-400 text-sm p-3 bg-yellow-900/20 rounded">
                  ⚠️ Aucun artiste trouvé. Créez d'abord un artiste (Formulaire 1 - 150$)
                </div>
              ) : (
                <Select
                  value={form2Data.artist_id}
                  onValueChange={(value) => setForm2Data(prev => ({ ...prev, artist_id: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Sélectionnez un artiste" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                    {artists.map((artist) => (
                      <SelectItem key={artist.id} value={artist.id}>
                        {artist.artist_name} ({artist.artist_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="text-white mb-2 block">Type de diffusion *</Label>
              <Select
                value={form2Data.release_type}
                onValueChange={(value) => setForm2Data(prev => ({ ...prev, release_type: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionnez un type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectItem value="Album">Album</SelectItem>
                  <SelectItem value="Single">Single</SelectItem>
                  <SelectItem value="EP">EP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Genre principal *</Label>
              <Select
                value={form2Data.main_genre}
                onValueChange={(value) => setForm2Data(prev => ({ ...prev, main_genre: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionnez un genre" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-60">
                  {musicGenres.map((genre) => (
                    <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Sous-genre</Label>
              <Input
                type="text"
                placeholder="Ex: Afro-fusion"
                value={form2Data.sub_genre}
                onChange={(e) => setForm2Data(prev => ({ ...prev, sub_genre: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Date de sortie *</Label>
                <Input
                  type="date"
                  value={form2Data.release_date}
                  onChange={(e) => setForm2Data(prev => ({ ...prev, release_date: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">Date de pré-sortie</Label>
                <Input
                  type="date"
                  value={form2Data.pre_release_date}
                  onChange={(e) => setForm2Data(prev => ({ ...prev, pre_release_date: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Description</Label>
              <Textarea
                placeholder="Décrivez votre chanson..."
                value={form2Data.description}
                onChange={(e) => setForm2Data(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Pays principal *</Label>
              <Input
                type="text"
                value={form2Data.main_country}
                onChange={(e) => setForm2Data(prev => ({ ...prev, main_country: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Upload Audio (WAV/MP3) *</Label>
              <Input
                type="file"
                onChange={(e) => setForm2Data(prev => ({ ...prev, audio_file: e.target.files[0] }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                accept=".wav,.mp3"
                required
              />
              {form2Data.audio_file && (
                <p className="text-xs text-green-400 mt-1">
                  ✓ {form2Data.audio_file.name}
                </p>
              )}
            </div>

            <div>
              <Label className="text-white mb-2 block">Upload Pochette (JPG 3000x3000) *</Label>
              <Input
                type="file"
                onChange={(e) => setForm2Data(prev => ({ ...prev, cover_image: e.target.files[0] }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                accept=".jpg,.jpeg"
                required
              />
              {form2Data.cover_image && (
                <p className="text-xs text-green-400 mt-1">
                  ✓ {form2Data.cover_image.name}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting || artists.length === 0}
              className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white font-bold py-6 text-lg`}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Distribution en cours...
                </>
              ) : (
                isAdminAdil ? 'GRATUIT (Admin) - Distribuer' : 'DISTRIBUER LA CHANSON - 30$'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL FORMULAIRE 3 - DISTRIBUTION VIDÉO VEVO (25$) */}
      <Dialog open={selectedModal === 'video'} onOpenChange={closeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              🎬 DISTRIBUTION VIDÉO VEVO - {isAdminAdil ? 'GRATUIT (Admin)' : '25$'}
            </DialogTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Distribuez votre vidéo sur VEVO, Apple Music, TIDAL
            </p>
          </DialogHeader>
          
          <form onSubmit={handleSubmitForm3} className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Titre de la vidéo *</Label>
              <Input
                type="text"
                placeholder="Ex: Mon nouveau clip"
                value={form3Data.video_title}
                onChange={(e) => setForm3Data(prev => ({ ...prev, video_title: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Artiste *</Label>
              {loadingArtists ? (
                <div className="text-zinc-400 text-sm">Chargement des artistes...</div>
              ) : artists.length === 0 ? (
                <div className="text-yellow-400 text-sm p-3 bg-yellow-900/20 rounded">
                  ⚠️ Aucun artiste trouvé. Créez d'abord un artiste (Formulaire 1 - 150$)
                </div>
              ) : (
                <Select
                  value={form3Data.artist_id}
                  onValueChange={(value) => setForm3Data(prev => ({ ...prev, artist_id: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Sélectionnez un artiste" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                    {artists.map((artist) => (
                      <SelectItem key={artist.id} value={artist.id}>
                        {artist.artist_name} ({artist.artist_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="text-white mb-2 block">Artistes en vedette</Label>
              <Input
                type="text"
                placeholder="Ex: Artiste 1, Artiste 2"
                value={form3Data.featured_artists}
                onChange={(e) => setForm3Data(prev => ({ ...prev, featured_artists: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Genre *</Label>
              <Select
                value={form3Data.genre}
                onValueChange={(value) => setForm3Data(prev => ({ ...prev, genre: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionnez un genre" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-60">
                  {musicGenres.map((genre) => (
                    <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Date de sortie *</Label>
              <Input
                type="date"
                value={form3Data.release_date}
                onChange={(e) => setForm3Data(prev => ({ ...prev, release_date: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Langue *</Label>
              <Select
                value={form3Data.language}
                onValueChange={(value) => setForm3Data(prev => ({ ...prev, language: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionnez une langue" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="French">French</SelectItem>
                  <SelectItem value="Lingala">Lingala</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Pays principal *</Label>
              <Input
                type="text"
                placeholder="Ex: Congo (DRC)"
                value={form3Data.main_country}
                onChange={(e) => setForm3Data(prev => ({ ...prev, main_country: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Piste associée</Label>
              <Input
                type="text"
                placeholder="Lien de la chanson associée"
                value={form3Data.associated_track}
                onChange={(e) => setForm3Data(prev => ({ ...prev, associated_track: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Upload Vidéo (MP4 HD 1080p) *</Label>
              <Input
                type="file"
                onChange={(e) => setForm3Data(prev => ({ ...prev, video_file: e.target.files[0] }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                accept=".mp4"
                required
              />
              {form3Data.video_file && (
                <p className="text-xs text-green-400 mt-1">
                  ✓ {form3Data.video_file.name}
                </p>
              )}
            </div>

            <div>
              <Label className="text-white mb-2 block">Upload Miniature *</Label>
              <Input
                type="file"
                onChange={(e) => setForm3Data(prev => ({ ...prev, thumbnail: e.target.files[0] }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                accept=".jpg,.jpeg,.png"
                required
              />
              {form3Data.thumbnail && (
                <p className="text-xs text-green-400 mt-1">
                  ✓ {form3Data.thumbnail.name}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting || artists.length === 0}
              className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white font-bold py-6 text-lg`}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Distribution en cours...
                </>
              ) : (
                isAdminAdil ? 'GRATUIT (Admin) - Distribuer' : 'DISTRIBUER LA VIDÉO - 25$'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bouton WhatsApp Flottant */}
      <a
        href="https://wa.me/243978475357?text=Bonjour%20AGRP%20BOX,%20j'ai%20besoin%20d'aide"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 flex items-center gap-2"
      >
        <span className="text-2xl">💬</span>
        <span className="hidden md:inline font-medium">PARLER AVEC AGRP BOX</span>
      </a>
    </div>
  );
};

export default DashboardPage;
